import { useState } from "react";
import type { AIState, SiteSpecContent } from "@imonsite/shared";
import { cn } from "../../lib/utils";
import { api } from "../../lib/api";
import { SubdomainInput } from "../SubdomainInput";

// Production uses custom domain, dev uses localhost
const SITE_SERVER_URL = import.meta.env.VITE_SITE_SERVER_URL || 
  (import.meta.env.PROD ? "https://preview.imonsite.co.uk" : "http://localhost:8790");

// Live site URL base
const LIVE_SITE_BASE = import.meta.env.VITE_LIVE_SITE_BASE || 
  (import.meta.env.PROD ? ".imonsite.co.uk" : ".localhost:8790");

interface ProgressSidebarProps {
  currentState: AIState;
  spec: Partial<SiteSpecContent>;
  siteId: string;
  currentBuildId?: string | null;
  subdomain?: string | null;
  onSubdomainChange?: (subdomain: string) => void;
  siteStatus?: string | null;
}

function hasRequiredFields(spec: Partial<SiteSpecContent>): boolean {
  return !!(
    spec.businessName &&
    spec.phone &&
    spec.email &&
    spec.services &&
    spec.services.length > 0 &&
    spec.coverageArea
  );
}

const STATES: { key: AIState; label: string; description: string; icon: string }[] = [
  {
    key: "collect_basics",
    label: "Basic Info",
    description: "Business name, services, contact",
    icon: "📋",
  },
  {
    key: "collect_style",
    label: "Style",
    description: "Tone and look preferences",
    icon: "🎨",
  },
  {
    key: "confirm",
    label: "Review",
    description: "Check everything is correct",
    icon: "✓",
  },
  {
    key: "generate_draft",
    label: "Building",
    description: "Creating your site",
    icon: "⚡",
  },
  {
    key: "iterate_edits",
    label: "Refining",
    description: "Making changes",
    icon: "✏️",
  },
  {
    key: "ready_to_publish",
    label: "Ready!",
    description: "Ready to go live",
    icon: "🚀",
  },
];

export function ProgressSidebar({ currentState, spec, siteId, currentBuildId, subdomain, onSubdomainChange, siteStatus }: ProgressSidebarProps) {
  const currentIndex = STATES.findIndex((s) => s.key === currentState);
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentBuildId ? `${SITE_SERVER_URL}/site/${siteId}` : null
  );
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const canBuild = hasRequiredFields(spec);
  const canPublish = !!(previewUrl && subdomain);
  const isPublished = siteStatus === "published";

  async function handleBuild() {
    if (!canBuild || isBuilding) return;

    setIsBuilding(true);
    setBuildError(null);

    try {
      const result = await api.buildSite(siteId);
      setPreviewUrl(result.previewUrl);
    } catch (err) {
      setBuildError(err instanceof Error ? err.message : "Build failed");
    } finally {
      setIsBuilding(false);
    }
  }

  async function handlePublish() {
    if (!canPublish || isPublishing) return;

    setIsPublishing(true);
    setPublishError(null);

    try {
      const { checkoutUrl } = await api.createCheckout(siteId);
      // Redirect to Stripe Checkout
      window.location.href = checkoutUrl;
    } catch (err) {
      setPublishError(err instanceof Error ? err.message : "Checkout failed");
      setIsPublishing(false);
    }
    // Don't set isPublishing to false on success - we're redirecting
  }

  const collectedFields = [
    spec.businessName && "Business name",
    spec.services?.length && `${spec.services.length} service${spec.services.length > 1 ? "s" : ""}`,
    spec.coverageArea && "Coverage area",
    spec.phone && "Phone",
    spec.email && "Email",
    spec.tone && "Tone preference",
  ].filter(Boolean);

  return (
    <aside className="hidden md:flex flex-col w-72 bg-secondary/30 border-r border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <a
          href="/"
          className="font-display text-xl font-medium tracking-tight text-primary hover:opacity-80 transition-opacity"
        >
          ImOnSite
        </a>
        <p className="mt-1 text-sm text-muted-foreground">Building your website</p>
      </div>

      {/* Progress steps */}
      <div className="flex-1 overflow-y-auto p-6">
        <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-4">
          Your Progress
        </h2>

        <ol className="space-y-1">
          {STATES.map((state, index) => {
            const isComplete = index < currentIndex;
            const isCurrent = index === currentIndex;
            const isPending = index > currentIndex;

            return (
              <li
                key={state.key}
                className={cn(
                  "relative flex items-start gap-3 py-3 px-3 rounded-lg transition-colors",
                  isCurrent && "bg-accent/10"
                )}
              >
                {/* Connecting line */}
                {index < STATES.length - 1 && (
                  <div
                    className={cn(
                      "absolute left-[26px] top-12 w-0.5 h-[calc(100%-24px)]",
                      isComplete ? "bg-primary" : "bg-border"
                    )}
                  />
                )}

                {/* Step indicator */}
                <div
                  className={cn(
                    "flex-shrink-0 size-8 rounded-full flex items-center justify-center text-sm font-medium",
                    isComplete && "bg-primary text-primary-foreground",
                    isCurrent && "bg-accent text-accent-foreground ring-2 ring-accent/20",
                    isPending && "bg-muted text-muted-foreground"
                  )}
                >
                  {isComplete ? (
                    <svg className="size-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    <span>{state.icon}</span>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  <span
                    className={cn(
                      "block text-sm font-medium",
                      isPending && "text-muted-foreground"
                    )}
                  >
                    {state.label}
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    {state.description}
                  </span>
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Collected info */}
      {collectedFields.length > 0 && (
        <div className="p-6 border-t border-border">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
            Collected so far
          </h3>
          <ul className="space-y-2">
            {collectedFields.map((field) => (
              <li
                key={field}
                className="flex items-center gap-2 text-sm text-foreground"
              >
                <svg
                  className="size-4 text-primary flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{field}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Subdomain section - show when user has a build ready */}
      {previewUrl && (
        <div className="p-6 border-t border-border">
          <SubdomainInput
            siteId={siteId}
            initialSubdomain={subdomain}
            onSubdomainReserved={onSubdomainChange}
          />
        </div>
      )}

      {/* Publish section - show when subdomain is reserved and we have a build */}
      {canPublish && (
        <div className="p-6 border-t border-border">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
            Go Live
          </h3>

          {isPublished ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 py-2 px-3 text-sm rounded-lg bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
                <svg className="size-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Site is live!</span>
              </div>
              <a
                href={`https://${subdomain}${LIVE_SITE_BASE}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <svg className="size-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z"
                    clipRule="evenodd"
                  />
                  <path
                    fillRule="evenodd"
                    d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z"
                    clipRule="evenodd"
                  />
                </svg>
                Visit {subdomain}{LIVE_SITE_BASE}
              </a>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Ready to publish?</p>
                <p>One-time setup: £9.99</p>
                <p>Monthly hosting: £9.99/mo</p>
              </div>

              <button
                onClick={handlePublish}
                disabled={isPublishing}
                className={cn(
                  "w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors",
                  "bg-accent text-accent-foreground hover:bg-accent/90",
                  "disabled:opacity-50 disabled:cursor-not-allowed"
                )}
              >
                {isPublishing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Redirecting to checkout...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="size-4" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Publish Site
                  </span>
                )}
              </button>

              {publishError && (
                <p className="text-xs text-destructive">{publishError}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Build Preview section */}
      <div className="p-6 border-t border-border">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
          Preview
        </h3>

        <div className="space-y-3">
          <button
            onClick={handleBuild}
            disabled={!canBuild || isBuilding}
            className={cn(
              "w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors",
              canBuild && !isBuilding
                ? "bg-primary text-primary-foreground hover:bg-primary/90"
                : "bg-muted text-muted-foreground cursor-not-allowed"
            )}
          >
            {isBuilding ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Building...
              </span>
            ) : (
              "Build Preview"
            )}
          </button>

          {buildError && (
            <p className="text-xs text-destructive">{buildError}</p>
          )}

          {!canBuild && !previewUrl && (
            <p className="text-xs text-muted-foreground">
              Complete the required fields to build a preview.
            </p>
          )}

          {previewUrl && (
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-lg text-sm font-medium border border-border hover:bg-accent/10 transition-colors"
            >
              <svg className="size-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path
                  fillRule="evenodd"
                  d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                  clipRule="evenodd"
                />
              </svg>
              View Preview
            </a>
          )}
        </div>
      </div>
    </aside>
  );
}

